import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CountryMark, SmsIcon } from '../components/sms/SmsMarks';
import {
  ApiError, addSharedLongRentalNumber, createPublicLongRentalInvoice,
  fetchPublicLongRentalInbox, fetchPublicLongRentalInvoiceStatus,
  fetchPublicLongRentalPaymentOptions, getApiErrorMessage,
  previewPublicLongRentalRenewal, wakePublicLongRental,
} from '../services/smsApi';
import { LongRentalRenewalPreview, PublicLongRentalInbox as PublicLongRentalInboxModel, PublicLongRentalInvoice } from '../types/models';
import { formatDateTime, formatUsd } from '../utils/format';
import { presentPhoneNumber } from '../utils/phone';

const PUBLIC_PATH_PATTERN = /\/inbox\/(1\d{10})\/?$/;
const LEGACY_PATH_PATTERN = /\/inbox\/([A-Za-z0-9_-]{43})\/(1\d{10})\/?$/;
const TOKEN_PATTERN = /^[A-Za-z0-9_-]{43}$/;

const STATE_LABELS: Record<PublicLongRentalInboxModel['state'], string> = {
  active: 'Номер активен',
  overdue: 'Продление ожидается',
  expired: 'Срок аренды истёк',
  refunded: 'Аренда завершена',
  unknown: 'Статус уточняется',
};

function readPublicPath(): { shareToken: string; phoneNumber: string } | null {
  if (typeof window === 'undefined') return null;
  const match = window.location.pathname.match(PUBLIC_PATH_PATTERN);
  const token = window.location.hash.slice(1);
  if (match && TOKEN_PATTERN.test(token)) return { shareToken: token, phoneNumber: match[1] };
  const legacy = window.location.pathname.match(LEGACY_PATH_PATTERN);
  return legacy ? { shareToken: legacy[1], phoneNumber: legacy[2] } : null;
}

const PublicLongRentalInbox: React.FC = () => {
  const access = useMemo(readPublicPath, []);
  const [inbox, setInbox] = useState<PublicLongRentalInboxModel | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState('');
  const [retryKey, setRetryKey] = useState(0);
  const [actionBusy, setActionBusy] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [renewalOpen, setRenewalOpen] = useState(false);
  const [preview, setPreview] = useState<LongRentalRenewalPreview | null>(null);
  const [paymentOptions, setPaymentOptions] = useState<Array<{ asset: string; network: string }>>([]);
  const [selectedPayment, setSelectedPayment] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [invoice, setInvoice] = useState<PublicLongRentalInvoice | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    if (!access) return;
    const key = `sms-public-renewal:${access.phoneNumber}`;
    const id = window.sessionStorage.getItem(key);
    if (!id) return;
    let cancelled = false;
    void fetchPublicLongRentalInvoiceStatus(access.shareToken, access.phoneNumber, id)
      .then((next) => { if (!cancelled) setInvoice(next); })
      .catch(() => { window.sessionStorage.removeItem(key); });
    return () => { cancelled = true; };
  }, [access]);

  const load = useCallback(async (quiet = false) => {
    if (!access) {
      setError('Публичная ссылка имеет неверный формат');
      setLoading(false);
      return false;
    }
    if (!quiet) setLoading(true);
    else setRefreshing(true);
    try {
      const next = await fetchPublicLongRentalInbox(access.shareToken, access.phoneNumber);
      if (mounted.current) {
        setInbox(next);
        setError('');
      }
      return true;
    } catch (loadError) {
      if (mounted.current) setError(getApiErrorMessage(loadError));
      return !(loadError instanceof ApiError && [404, 410].includes(loadError.status));
    } finally {
      if (mounted.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }, [access]);

  useEffect(() => {
    mounted.current = true;
    const previousTitle = document.title;
    document.title = 'Публичный приём SMS';
    let timer: number | null = null;
    let stopped = false;
    const tick = async () => {
      const keepPolling = document.visibilityState !== 'visible' || await load(true);
      if (!stopped && keepPolling) timer = window.setTimeout(tick, 10_000);
    };
    void load().then((keepPolling) => {
      if (!stopped && access && keepPolling) timer = window.setTimeout(tick, 10_000);
    });
    return () => {
      stopped = true;
      mounted.current = false;
      document.title = previousTitle;
      if (timer) window.clearTimeout(timer);
    };
  }, [access, load, retryKey]);

  useEffect(() => {
    const invoiceId = invoice?.id;
    if (!access || !invoiceId || !['invoice_pending', 'payment_confirmed', 'provider_in_flight'].includes(invoice?.state || '')) return;
    let stopped = false;
    let timer: number | null = null;
    const tick = async () => {
      try {
        const next = await fetchPublicLongRentalInvoiceStatus(access.shareToken, access.phoneNumber, invoiceId);
        if (!stopped) {
          setInvoice(next);
          if (next.state === 'completed') void load(true);
        }
      } catch { /* следующий опрос повторит проверку */ }
      if (!stopped) timer = window.setTimeout(tick, 10_000);
    };
    timer = window.setTimeout(tick, 10_000);
    return () => { stopped = true; if (timer) window.clearTimeout(timer); };
  }, [access, invoice?.id, invoice?.state, load]);

  const activate = async () => {
    if (!access || actionBusy) return;
    setActionBusy(true); setActionMessage('');
    try {
      const next = await wakePublicLongRental(access.shareToken, access.phoneNumber);
      setInbox(next);
      setActionMessage('Номер активирован. Проверьте указанное время приёма SMS.');
    } catch (actionError) { setActionMessage(getApiErrorMessage(actionError)); }
    finally { setActionBusy(false); }
  };

  const addToMyNumbers = async () => {
    if (!access || actionBusy) return;
    setActionBusy(true); setActionMessage('');
    try {
      const result = await addSharedLongRentalNumber(access.shareToken, access.phoneNumber);
      setActionMessage(result.alreadyOwned ? 'Этот номер уже находится в вашем кабинете.' : 'Ссылка добавлена в «Мои номера». Владение номером не меняется.');
    } catch (actionError) {
      setActionMessage(actionError instanceof ApiError && actionError.status === 401
        ? 'Для добавления в «Мои номера» войдите в свой аккаунт. Просмотр SMS доступен без входа.'
        : getApiErrorMessage(actionError));
    } finally { setActionBusy(false); }
  };

  const openRenewal = async () => {
    if (!access || actionBusy) return;
    setRenewalOpen(true); setActionBusy(true); setActionMessage('');
    try {
      const [nextPreview, options] = await Promise.all([
        previewPublicLongRentalRenewal(access.shareToken, access.phoneNumber),
        fetchPublicLongRentalPaymentOptions(access.shareToken, access.phoneNumber),
      ]);
      setPreview(nextPreview); setPaymentOptions(options);
      setSelectedPayment(options[0] ? `${options[0].asset}:${options[0].network}` : '');
      setTermsAccepted(false);
    } catch (actionError) { setActionMessage(getApiErrorMessage(actionError)); }
    finally { setActionBusy(false); }
  };

  const issueInvoice = async () => {
    if (!access || !preview || !termsAccepted || actionBusy) return;
    const option = paymentOptions.find((item) => `${item.asset}:${item.network}` === selectedPayment);
    if (!option) return;
    setActionBusy(true); setActionMessage('');
    try {
      const created = await createPublicLongRentalInvoice(
        access.shareToken, access.phoneNumber, option.asset, option.network, preview.finalAmount,
      );
      setInvoice(created);
      window.sessionStorage.setItem(`sms-public-renewal:${access.phoneNumber}`, created.id);
    } catch (actionError) { setActionMessage(getApiErrorMessage(actionError)); }
    finally { setActionBusy(false); }
  };

  const phone = inbox ? presentPhoneNumber(inbox.phoneNumber) : null;
  const messages = inbox && Array.isArray(inbox.messages) ? inbox.messages : [];

  return (
    <main className="sms-publicInbox">
      <section className="sms-publicInbox__card" aria-labelledby="public-inbox-title">
        <header className="sms-publicInbox__header">
          <div className="sms-publicInbox__brand"><SmsIcon name="message" /><span>Публичный приём SMS</span></div>
          {inbox ? <span className={`sms-longNumber__state is-${inbox.state}`}>{STATE_LABELS[inbox.state] || 'Статус уточняется'}</span> : null}
        </header>

        {loading ? <div className="sms-publicInbox__loading" role="status">Загружаем сообщения…</div> : null}
        {!loading && error ? (
          <div className="sms-publicInbox__error" role="alert">
            <SmsIcon name="cloud" />
            <h1 id="public-inbox-title">Сообщения недоступны</h1>
            <p>{error}</p>
            <button type="button" className="u2-btn is-secondary" onClick={() => setRetryKey((value) => value + 1)}>Повторить</button>
          </div>
        ) : null}

        {!loading && inbox && phone ? (
          <>
            <div className="sms-publicInbox__number">
              <div className="sms-longNumber__marks"><CountryMark name={inbox.geoName} /></div>
              <span><small>Номер для получения SMS</small><h1 id="public-inbox-title">{phone.displayFullNumber}</h1></span>
              <button type="button" className="u2-btn is-secondary is-sm" disabled={refreshing} onClick={() => void load(true)}>{refreshing ? 'Обновляем…' : 'Обновить'}</button>
            </div>
            <div className="sms-publicInbox__meta">
              <span>{inbox.endsAt ? `Аренда до ${formatDateTime(inbox.endsAt)}` : 'Срок аренды уточняется'}</span>
              <span>{inbox.lastSyncedAt ? `Проверено ${formatDateTime(inbox.lastSyncedAt)}` : 'Ожидаем синхронизацию'}</span>
            </div>
            <div className="sms-publicInbox__actions">
              {inbox.wake?.available && inbox.wake.status === 'sleeping' ? (
                <button type="button" className="u2-btn is-primary" disabled={actionBusy} onClick={() => void activate()}>
                  {actionBusy ? 'Активируем…' : 'Активировать номер'}
                </button>
              ) : null}
              {inbox.wake?.status === 'ready' && inbox.wake.readyUntil ? <span>Приём активен до {formatDateTime(inbox.wake.readyUntil)}</span> : null}
              {inbox.wake?.status === 'scheduled' && inbox.wake.scheduledFor ? <span>Приём начнётся {formatDateTime(inbox.wake.scheduledFor)}</span> : null}
              {inbox.renewalAvailable ? <button type="button" className="u2-btn is-secondary" disabled={actionBusy} onClick={() => void openRenewal()}>Продлить номер</button> : null}
              {invoice ? <button type="button" className="u2-btn is-secondary" onClick={() => setRenewalOpen(true)}>Открыть счёт</button> : null}
              <button type="button" className="u2-btn is-secondary" disabled={actionBusy} onClick={() => void addToMyNumbers()}>Добавить в мои номера</button>
            </div>
            {actionMessage ? <p className="sms-publicInbox__actionMessage" role="status">{actionMessage}</p> : null}
            <section className="sms-publicInbox__stream" aria-label={`Сообщения номера ${phone.displayFullNumber}`}>
              <div className="sms-longNumber__streamHead"><strong>Все сообщения</strong><span>{messages.length}</span></div>
              {messages.length ? messages.map((message) => (
                <article className="sms-longNumber__message" key={message.id}>
                  <div><strong>{message.sender || 'Отправитель не указан'}</strong><time dateTime={message.receivedAt}>{formatDateTime(message.receivedAt)}</time></div>
                  <p>{message.text}</p>
                </article>
              )) : (
                <div className="sms-longNumber__waiting"><span aria-hidden="true" /><p><strong>Сообщений пока нет</strong><small>Страница обновляется автоматически</small></p></div>
              )}
            </section>
            <p className="sms-publicInbox__privacy">Любой человек с этой ссылкой может видеть SMS, активировать номер и оплатить продление. Баланс и кабинет владельца недоступны. Владелец может отозвать ссылку.</p>
          </>
        ) : null}
      </section>
      {renewalOpen ? (
        <div className="sms-publicInbox__modalBackdrop" role="presentation">
          <section className="sms-publicInbox__modal" role="dialog" aria-modal="true" aria-labelledby="public-renew-title" onKeyDown={(event) => { if (event.key === 'Escape') setRenewalOpen(false); }}>
            <header><div><small>Продление номера</small><h2 id="public-renew-title">Отдельный счёт без списания с владельца</h2></div>
              <button type="button" className="u2-btn is-secondary is-sm" autoFocus onClick={() => setRenewalOpen(false)}>Закрыть</button></header>
            {actionBusy && !preview ? <p role="status">Проверяем цену и способы оплаты…</p> : null}
            {preview && !invoice ? <>
              <p className="sms-publicInbox__renewPrice">{formatUsd(preview.finalAmount)} <small>на {preview.durationDays} дней</small></p>
              {preview.paymentMinimumUsd && preview.finalAmount < preview.paymentMinimumUsd ? <p className="sms-inlineAlert is-error">Отдельный счёт сейчас доступен от {formatUsd(preview.paymentMinimumUsd)}. Для продления этого номера обратитесь в поддержку.</p> : null}
              <div className="sms-publicInbox__terms">
                <p>{preview.terms.rental}</p><p>{preview.terms.renewal}</p><p>{preview.terms.cancellation}</p><p>{preview.terms.refund}</p>
              </div>
              <p>После выставления счёта проверьте точную сумму перевода: для распознавания платежа она может немного отличаться от цены продления.</p>
              <label className="sms-field"><span>Способ оплаты</span><select className="u2-input" value={selectedPayment} onChange={(event) => setSelectedPayment(event.target.value)}>
                {paymentOptions.map((option) => <option key={`${option.asset}:${option.network}`} value={`${option.asset}:${option.network}`}>{option.asset} · {option.network}</option>)}
              </select></label>
              <label className="sms-publicInbox__accept"><input type="checkbox" checked={termsAccepted} onChange={(event) => setTermsAccepted(event.target.checked)} /> Я прочитал условия и подтверждаю выставление счёта</label>
              <button type="button" className="u2-btn is-primary" disabled={!termsAccepted || !selectedPayment || actionBusy || Boolean(preview.paymentMinimumUsd && preview.finalAmount < preview.paymentMinimumUsd)} onClick={() => void issueInvoice()}>
                {actionBusy ? 'Выставляем счёт…' : 'Выставить счёт'}
              </button>
            </> : null}
            {invoice ? <div className="sms-publicInbox__invoice">
              <strong>{invoice.state === 'completed' ? 'Номер продлён' : invoice.state === 'invoice_pending' ? 'Ожидаем оплату' : invoice.state === 'provider_in_flight' || invoice.state === 'payment_confirmed' ? 'Оплата получена, продлеваем номер' : invoice.state === 'expired' ? 'Срок счёта истёк' : 'Операция требует проверки'}</strong>
              {invoice.state === 'invoice_pending' && invoice.payment ? <>
                <p>Переведите ровно <b>{invoice.payment.amount} {invoice.payment.asset}</b> в сети <b>{invoice.payment.network}</b> на адрес:</p>
                <code>{invoice.payment.payAddress}</code>
                <button type="button" className="u2-btn is-secondary is-sm" onClick={() => void navigator.clipboard.writeText(invoice.payment!.payAddress)}>Копировать адрес</button>
                <small>Счёт действует до {formatDateTime(invoice.payment.expiresAt)}. После подтверждения оплаты срок обновится автоматически.</small>
              </> : null}
              {invoice.state === 'invoice_pending' && !invoice.payment ? <p>Реквизиты счёта временно недоступны. Не создавайте новый счёт; повторите загрузку страницы позже.</p> : null}
              {['manual_review', 'refund_due'].includes(invoice.state) ? <p>Не оплачивайте повторно. Напишите в поддержку и сообщите ID счёта {invoice.id}. Средства владельца номера не затрагиваются.</p> : null}
            </div> : null}
            {actionMessage ? <p className="sms-inlineAlert is-error" role="alert">{actionMessage}</p> : null}
          </section>
        </div>
      ) : null}
    </main>
  );
};

export default PublicLongRentalInbox;
